/**
 * auth-forms.js — auth 5 頁互動（register / forgot-password / reset-password）
 *
 * 對照 HTML（src/pages/auth/*.html）：
 *   register.html  : <form data-auth-register> + [data-auth-tabs] 雙 tab（會員/診所 panel）
 *                    + 同意隱私 checkbox（#register-agree）gating submit
 *                    + 密碼 #register-pwd（≥8）/ 確認 #register-pwd-confirm 即時驗證
 *   forgot/reset   : <form data-auth-pwd-form data-auth-success="#…" | data-auth-redirect="/…">
 *                    forgot 送出後顯 inline .admin-alert--info；reset 成功跳轉 reset-complete
 *
 * 功能（MEMBER-SPEC §C6 / §C8 / §C9）：
 *   - register tab 切換：is-active + aria-selected 同步，panel hidden 顯隱，
 *     roving tabindex + ← → Home End 鍵盤導覽（鏡像 member-favorites-tab.js 的 activate）。
 *   - checkbox gating：未勾 → submit disabled（aria-disabled + disabled）；勾 → enable。
 *     防呆：submit 時仍未勾 → error「請先閱讀並同意隱私權政策」（role=alert）。
 *   - 密碼即時驗證：新密碼 ≥8（input/blur）、確認密碼須相符（鏡像 member-edit.js）。
 *   - submit：HTML5 checkValidity + 自訂規則 → 通過則 .is-submitting + inline spinner
 *     （reduced-motion 退「處理中…」純文字）。forgot 模擬成功顯 inline alert；
 *     reset 模擬成功跳轉；register 模擬成功跳轉 register-complete（由 form action 帶）。
 *     本波無 backend，submit 不真正送出；接後端時把 simulate 換成 fetch。
 *
 * 沿用既有慣例：form-field--invalid + [aria-invalid] 標紅（form-field.css）、
 *   spinner 與 reduced-motion 文字退場（對齊 member-edit.js）、escapeHtml 不需要
 *   （本檔不把使用者輸入插回 innerHTML）。
 * 無對應 DOM（[data-auth-register] / [data-auth-pwd-form] 皆不存在）即 no-op，
 *   可安全在所有 admin/auth/member 頁載入（隨 admin.js）。
 *
 * 密碼即時檢核清單（.pw-checklist）共用 password-checklist.js（register 密碼欄 + reset 新密碼欄），
 * 取代原靜態 hint。清單三項硬性擋送出（≥8 / 含英文 / 含數字）+ 確認一致，併入既有 gate。
 */

import { initPasswordChecklist } from './password-checklist.js';
import { prefersReducedMotion } from '../utils/dom.js';
// 欄位 invalid 標記「三件套」→ 收斂至 field-error.js（單一真相源）。新簽名 setInvalid(control, message) /
// clearInvalid(control)：error 節點由 helper 依 .form-field 包裹自解（不再傳入）。
// auth confirm 欄 HTML 已靜態帶 aria-describedby="…-confirm-error"（= error 節點 id）且欄內無
// .form-field__hint，故 helper 的 describedby 切換指向同一 id（no-op）、無 hint 可收，行為與原內聯等價；
// 純密碼欄（無 __error 節點）走 message-null 路徑只切 aria-invalid + 紅框，亦等價。
import { setInvalid, clearInvalid } from './field-error.js';

const SPINNER_SVG =
  '<svg class="member-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
  'stroke-dasharray="42" stroke-dashoffset="14" /></svg>';

/* ---------- 密碼顯示/隱藏 toggle（重用 member-edit.js 同模式 / .member-pwd-field）----------
 * reset-password 兩密碼欄套 .member-pwd-field markup（eye + eye-off 兩 icon）。
 * SVGElement 不反射 .hidden IDL 屬性，故用 toggleAttribute('hidden') 切（CSS
 * .member-pwd-field__toggle svg[hidden] 才命中）—— 與 member-edit.js 鏡像，禁用 .hidden property。 */
function initPwdToggles(root) {
  root.querySelectorAll('[data-pwd-toggle]').forEach((btn) => {
    const input = document.getElementById(btn.getAttribute('data-pwd-toggle'));
    if (!input) return;
    const eye = btn.querySelector('.member-pwd-field__eye');
    const eyeOff = btn.querySelector('.member-pwd-field__eye-off');
    const setHidden = (el, hide) => el && el.toggleAttribute('hidden', hide);
    btn.addEventListener('click', () => {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.setAttribute('aria-pressed', show ? 'true' : 'false');
      btn.setAttribute('aria-label', show ? '隱藏密碼' : '顯示密碼');
      setHidden(eye, show);
      setHidden(eyeOff, !show);
    });
  });
}

/* ---------- submit loading 進入 / 退出（對齊 member-edit.js） ---------- */
function enterSubmitting(form, submitBtn, labelText) {
  form.classList.add('is-submitting');
  submitBtn.setAttribute('aria-disabled', 'true');
  submitBtn.disabled = true;
  submitBtn.innerHTML = prefersReducedMotion()
    ? '處理中…'
    : SPINNER_SVG + '<span>' + labelText + '</span>';
}
function exitSubmitting(form, submitBtn, originalLabel) {
  form.classList.remove('is-submitting');
  submitBtn.removeAttribute('aria-disabled');
  submitBtn.disabled = false;
  submitBtn.textContent = originalLabel;
}

/* =====================================================================
 * Tab（register 雙 panel）— 鏡像 member-favorites-tab.js 的 activate / roving tabindex
 * ===================================================================== */
function initTabs(tablist) {
  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  if (!tabs.length) return;
  const panelFor = (tab) => document.getElementById(tab.getAttribute('aria-controls'));

  function activate(tab, { focus = false } = {}) {
    tabs.forEach((t) => {
      const selected = t === tab;
      t.classList.toggle('is-active', selected);
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
      const panel = panelFor(t);
      if (panel) {
        if (selected) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      }
    });
    if (focus) tab.focus();
  }

  tablist.addEventListener('click', (e) => {
    const tab = e.target.closest('[role="tab"]');
    if (tab && tabs.includes(tab)) activate(tab);
  });

  tablist.addEventListener('keydown', (e) => {
    const current = e.target.closest('[role="tab"]');
    if (!current || !tabs.includes(current)) return;
    const idx = tabs.indexOf(current);
    let next;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = tabs[(idx + 1) % tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = tabs[(idx - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }
    e.preventDefault();
    activate(next, { focus: true });
  });
}

/* =====================================================================
 * register.html — 雙 tab + 同意 gating + 密碼即時驗證
 * ===================================================================== */
function initRegister(form) {
  const tablist = document.querySelector('[data-auth-tabs]');
  if (tablist) initTabs(tablist);

  const refs = {
    pwd: form.querySelector('#register-pwd'),
    confirm: form.querySelector('#register-pwd-confirm'),
    confirmError: form.querySelector('#register-pwd-confirm-error'),
    agree: form.querySelector('#register-agree'),
    agreeError: form.querySelector('#register-agree-error'),
  };
  const submitBtn = form.querySelector('[data-auth-submit]');
  if (!submitBtn) return;
  // 註：originalLabel（還原鈕文字）僅退場/失敗還原路徑才需要；DEMO bypass 導頁不還原，故不取。
  //   接 backend 還原 gating 時，submit 失敗分支需 exitSubmitting(form, submitBtn, originalLabel)，屆時補回。

  /* 即時驗證：新密碼 ≥8（非空才驗，空交 required）/ 確認相符 */
  const validatePwd = () => {
    if (refs.pwd.value && refs.pwd.value.length < 8) {
      setInvalid(refs.pwd); // message-null：純密碼欄無 __error，只切紅框 + aria-invalid
      return false;
    }
    clearInvalid(refs.pwd);
    return true;
  };
  const validateConfirm = () => {
    if (refs.confirm.value && refs.confirm.value !== refs.pwd.value) {
      setInvalid(refs.confirm, '兩次輸入的密碼不一致');
      return false;
    }
    clearInvalid(refs.confirm);
    return true;
  };
  refs.pwd.addEventListener('input', () => {
    validatePwd();
    if (refs.confirm.value) validateConfirm();
  });
  refs.confirm.addEventListener('input', validateConfirm);

  /* checkbox gating + 密碼清單 gating：
     三項規則皆 met（≥8 / 含英文 / 含數字）AND 兩次一致 AND 同意 checkbox 已勾 → enable submit。
     清單即時狀態由 password-checklist.js 維護；其 onChange 觸發 syncGate 重算 disabled。
     先宣告 checklist 容器（let）再宣告 syncGate，最後 init 清單——避免 init 同步評估時的 TDZ。 */
  let checklist = null;
  const syncGate = () => {
    const ok =
      refs.agree.checked && (checklist ? checklist.isValid() : true);
    submitBtn.disabled = !ok;
    submitBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    if (refs.agree.checked && refs.agreeError) {
      refs.agreeError.textContent = '';
      refs.agreeError.hidden = true;
    }
  };
  checklist = initPasswordChecklist({
    pwd: refs.pwd,
    confirm: refs.confirm,
    confirmError: refs.confirmError,
    onChange: syncGate,
  });
  refs.agree.addEventListener('change', syncGate);

  // TODO(backend): 移除 DEMO BYPASS 還原 gating，submit 改打 POST /auth/register-complete（建會員+寄信+唯一性） — 見 docs/BACKEND_HANDOFF.md#auth-register
  /* ════════════════════════════════════════════════════════════════════
   * DEMO BYPASS 2026-06-22：跳過驗證直接導頁，接 backend 時還原 gating
   * （慣例同 src/scripts/pages/login-demo.js 的 DEMO BYPASS）
   * ────────────────────────────────────────────────────────────────────
   * 用戶決策（方案1）：按「註冊」直通成功頁，不做任何資料驗證（demo 無後端）。
   *   - 按鈕載入即 ENABLED（不跑 syncGate gating）；
   *   - submit 跳過全部驗證（不呼叫 checkValidity / validatePwd / validateConfirm / agree gating），
   *     僅保留既有 enterSubmitting spinner + ~900ms setTimeout，再導向成功頁。
   *   - validatePwd / validateConfirm / syncGate 與 checklist 仍保留並 wired（上方即時 UI 不變），
   *     只是 submit 路徑不調用——還原 gating 時把 DEMO 區塊換回原 syncGate() + 完整 submit 驗證即可。
   *
   * 導頁路徑（子目錄部署正確性）：本站部署於子路徑
   *   （https://demo.nsdi.com.tw/2026-medproof/dist/），form action="/auth/register-complete"
   *   是 ROOT-ABSOLUTE，window.location.href 餵它會解析到「網域根」demo.nsdi.com.tw/auth/... → 404，
   *   且 postprocess-dist.js 只改寫 <a>/<area> 的 href、不改 form action、也不碰 JS 檔內字面值
   *   （該腳本檔頭明載「只讀寫 *.html」）。故改用「相對當前頁」的 .html 路徑：register.html 與
   *   register-complete.html 同在 dist/auth/ 下，瀏覽器以當前頁為基準解析 'register-complete.html'
   *   即得 .../auth/register-complete.html，子路徑任意深度都正確（鏡像 login-demo.js 的 data-login-target
   *   相對路徑作法）。勿改回絕對 /auth/...（會 404）。
   *
   * TODO(backend): 接後端時：(1) 移除本 DEMO 區塊、還原 submit 完整驗證 + 初始 syncGate() disabled；
   *   (2) submit 改打註冊 API（建立會員 + 寄驗證信 + email 唯一性檢查），成功 resolve 後才導頁，
   *   失敗則 exitSubmitting + 以 setInvalid 顯後端錯誤；(3) 視需要拿掉 register.html 的 novalidate。 */
  const SUCCESS_HREF = 'register-complete.html'; // 相對 register.html（同 dist/auth/）；勿改絕對路徑
  submitBtn.disabled = false;
  submitBtn.setAttribute('aria-disabled', 'false');

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (form.classList.contains('is-submitting')) return;

    // DEMO：跳過全部驗證，直接 spinner → 導成功頁（相對路徑，子目錄安全）。
    enterSubmitting(form, submitBtn, '註冊中…');
    window.setTimeout(() => {
      window.location.href = SUCCESS_HREF;
    }, 900);
  });
  /* ═══════════════════════ DEMO BYPASS 區塊結束 ═══════════════════════ */
}

/* =====================================================================
 * forgot-password / reset-password — 單表單 submit
 *   data-auth-success="#sel" → 送出後顯該 inline alert（forgot）
 *   data-auth-redirect="/path" → 成功跳轉（reset → reset-complete）
 *   data-auth-pwd="confirm"  → 啟用「新密碼 ≥8 + 確認相符」雙欄驗證（reset）
 * ===================================================================== */
function initPwdForm(form) {
  const submitBtn = form.querySelector('[data-auth-submit]');
  if (!submitBtn) return;
  const originalLabel = submitBtn.textContent;

  // P2-15：reset-password 兩密碼欄顯示/隱藏 toggle（forgot 無密碼欄則無 [data-pwd-toggle]，no-op）。
  initPwdToggles(form);

  const successSel = form.getAttribute('data-auth-success');
  const redirect = form.getAttribute('data-auth-redirect');
  const hasPwdPair = form.getAttribute('data-auth-pwd') === 'confirm';

  const refs = {
    pwd: form.querySelector('#reset-pwd'),
    confirm: form.querySelector('#reset-pwd-confirm'),
    confirmError: form.querySelector('#reset-pwd-confirm-error'),
  };

  const validatePwd = () => {
    if (!hasPwdPair) return true;
    if (refs.pwd.value && refs.pwd.value.length < 8) {
      setInvalid(refs.pwd); // message-null：純密碼欄無 __error，只切紅框 + aria-invalid
      return false;
    }
    clearInvalid(refs.pwd);
    return true;
  };
  const validateConfirm = () => {
    if (!hasPwdPair) return true;
    if (refs.confirm.value && refs.confirm.value !== refs.pwd.value) {
      setInvalid(refs.confirm, '兩次輸入的密碼不一致');
      return false;
    }
    clearInvalid(refs.confirm);
    return true;
  };
  // 密碼清單 + 一致指示（reset 啟用，data-auth-pwd="confirm"）。
  // gate：三項皆 met AND 兩次一致 → enable submit（reset 無 checkbox，故只看清單）。
  let checklist = null;
  const syncGate = () => {
    if (!checklist) return;
    const ok = checklist.isValid();
    submitBtn.disabled = !ok;
    submitBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
  };
  if (hasPwdPair && refs.pwd && refs.confirm) {
    refs.pwd.addEventListener('input', () => {
      validatePwd();
      if (refs.confirm.value) validateConfirm();
    });
    refs.confirm.addEventListener('input', validateConfirm);
    checklist = initPasswordChecklist({
      pwd: refs.pwd,
      confirm: refs.confirm,
      confirmError: refs.confirmError,
      onChange: syncGate,
    });
    syncGate(); // 初始：密碼未達 → disabled
  }

  // TODO(backend): reset 改 fetch POST 提交新密碼 + 驗 query token，resolve 後才跳轉 — 見 docs/BACKEND_HANDOFF.md#auth-reset-password
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (form.classList.contains('is-submitting')) return;

    let firstInvalid = null;
    form.querySelectorAll('.form-field__input').forEach((field) => {
      if (field.disabled) return;
      clearInvalid(field);
      if (!field.checkValidity()) {
        setInvalid(field); // message-null：僅紅框 + aria-invalid，文字訊息交後續 validateConfirm
        if (!firstInvalid) firstInvalid = field;
      }
    });
    if (!validatePwd() && !firstInvalid) firstInvalid = refs.pwd;
    if (!validateConfirm() && !firstInvalid) firstInvalid = refs.confirm;

    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    // TODO(backend): 純前端示意。forgot 接後端改 fetch 寄重設信、reset 改 fetch 提交新密碼，
    //   resolve 後才 redirect / 顯成功 alert；失敗 exitSubmitting + 後端錯誤訊息。
    // TODO(backend): forgot 改 fetch POST 寄重設信，resolve 後才顯成功 alert（回應防帳號列舉） — 見 docs/BACKEND_HANDOFF.md#auth-forgot-password
    enterSubmitting(form, submitBtn, '處理中…');
    window.setTimeout(() => {
      if (redirect) {
        window.location.href = redirect;
        return;
      }
      // forgot：留在頁面，顯 inline alert + 還原鈕（可重送）
      exitSubmitting(form, submitBtn, originalLabel);
      if (successSel) {
        const alertEl = document.querySelector(successSel);
        if (alertEl) {
          alertEl.hidden = false;
          // 移動焦點到提示，AT 立即朗讀（alert 容器 role=status / tabindex=-1）
          if (typeof alertEl.focus === 'function') alertEl.focus();
        }
      }
    }, 900);
  });
}

/* ---------- 成功頁抵達移焦（register-complete.html，a11y）----------
 * 註冊成功頁無表單 / 無頁面專屬 JS（entry 僅 admin.js），故抵達時的移焦掛在此共用模組。
 * .auth-success 已標 role=status + aria-live=polite（AT 朗讀成功訊息），再把焦點移到
 * 成功標題 #rc-heading（tabindex=-1），讓鍵盤/AT 使用者抵達即定位於成功語境、後續 Tab
 * 從標題往下走（前往登入鈕等）。requestAnimationFrame 內移焦——等版面就緒避免捲動跳動，
 * 鏡像本檔 forgot 流程的 alertEl.focus() 慣例。無 #rc-heading（其他頁）即 no-op。 */
function initSuccessArrival(root = document) {
  const heading = root.querySelector('.auth-success #rc-heading[tabindex="-1"]');
  if (!heading) return;
  requestAnimationFrame(() => heading.focus());
}

/* ---------- init ---------- */
function init(root = document) {
  const register = root.querySelector('[data-auth-register]');
  if (register) initRegister(register);

  root.querySelectorAll('[data-auth-pwd-form]').forEach((form) => initPwdForm(form));

  initSuccessArrival(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
